import { z } from 'zod';

import { Nullable } from '@clearverify/types';
import { convertCentsToDollars } from '@clearverify/utils';

import { RevenueShareType } from 'src/api';
import { RevenueShareSettingHost } from 'src/hooks/revenueShare';

import { RevenueShareFormField } from './constants';
import { RevenueShareFormValues } from './types';

const NO_MORE_THAN_TWO_DECIMAL_DIGITS_REGEXP = /^\d+(\.\d{0,2})?$/;

const revenueShareValidationSchema = z.object({
  [RevenueShareFormField.revenueShareEnabled]: z.boolean(),
  [RevenueShareFormField.revenueShareReportEnabled]: z.boolean(),
});

const revenueShareFlatValidationSchema = z.object({
  [RevenueShareFormField.revenueShareType]: z.literal(RevenueShareType.Flat),
  [RevenueShareFormField.revenueShareValue]: z
    .number('Required')
    .positive('Should be positive')
    .refine(value => NO_MORE_THAN_TWO_DECIMAL_DIGITS_REGEXP.test(value.toString()), {
      error: 'Must have no more than 2 digits after decimal',
    }),
});

const revenueSharePercentsValidationSchema = z.object({
  [RevenueShareFormField.revenueShareType]: z.literal(RevenueShareType.Percents),
  [RevenueShareFormField.revenueShareValue]: z
    .number('Required')
    .positive('Should be positive')
    .max(100, 'The value should not be more than 100')
    .refine(value => NO_MORE_THAN_TWO_DECIMAL_DIGITS_REGEXP.test(value.toString()), {
      error: 'Must have no more than 2 digits after decimal',
    }),
});

export const revenueShareFormValidationSchema = revenueShareValidationSchema.and(
  z.discriminatedUnion(RevenueShareFormField.revenueShareType, [
    revenueShareFlatValidationSchema,
    revenueSharePercentsValidationSchema,
  ]),
) satisfies z.ZodType<RevenueShareFormValues>;

export const getRevenueShareInitialValues = (
  company: Nullable<RevenueShareSettingHost & { revenueShareReportEnabled: boolean }>,
): RevenueShareFormValues => {
  const setting = company?.revenueShareSetting;

  return {
    [RevenueShareFormField.revenueShareEnabled]: Boolean(setting),
    [RevenueShareFormField.revenueShareType]: setting?.revenueShareType || RevenueShareType.Flat,
    [RevenueShareFormField.revenueShareValue]:
      (setting?.revenueShareType === RevenueShareType.Flat
        ? convertCentsToDollars(setting.revenueShareFlat)
        : setting?.revenueSharePercents) || 0,
    [RevenueShareFormField.revenueShareReportEnabled]: company?.revenueShareReportEnabled || false,
  };
};
